import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SCTCustomTable } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/sct-table.component';
import { DeviceService } from 'src/app/home/site-dashboard/device/device.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { UsersService } from 'src/app/users/users.service';

interface BlockedDevice {
	mac_address: string;
	serial_number: string;
	fw_version: number;
	device_type: string | null;
	site_id: number;
	site_name: string;
	customer_id: number;
	customer_name: string;
	/** Epoch seconds */
	last_connect_time: number | null;
	/** Epoch seconds */
	blocked_until: number;
}

interface UnblockDevicesResult {
	unblocked: string[];
	failed: string[];
}

@Component({
	selector: 'app-blocked-devices',
	templateUrl: './blocked-devices.component.html'
})
export class BlockedDevicesComponent implements OnInit, AfterViewInit {
	@ViewChild('sctCustomTable') sctCustomTable: SCTCustomTable;
	@ViewChild('unblockConfirmationPopup') unblockConfirmationPopup: ModalComponent;

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		blocked_until: TimeSource.PcTime,
		last_connect_time: TimeSource.PcTime,
	};

	// The table's dropdown filter on a string column matches by "contains", so "TrukLink" would also
	// match "TrukLink PS". A number column with numeric values gets an exact match instead.
	private readonly deviceTypeItems = this.deviceService.connectivityDeviceTypes
		.map((type, index) => ({ label: type.label, value: index + 1 }));

	columnConfig: ColumnsConfig[] = [
		{ key: 'id', name: 'id', type: 'id', hidden: true },
		{ key: 'customer_name', name: this.translate.instant('g.customer_name'), type: 'link' },
		{ key: 'site_name', name: this.translate.instant('g.site_name'), type: 'link' },
		{
			key: 'device_type', name: this.translate.instant('devices.device_type'), type: 'number',
			filterType: 'dropdown', items: this.deviceTypeItems
		},
		{ key: 'serial_number', name: this.translate.instant('g.serial_number'), type: 'string' },
		{ key: 'mac_address', name: this.translate.instant('g.mac_address'), type: 'link' },
		{ key: 'fw_version', name: this.translate.instant('devices.fw_version'), type: 'number' },
		{ key: 'blocked_until', name: this.withTimeZone('devices.blocked_connection_until'), type: 'date' },
		{ key: 'last_connect_time', name: this.withTimeZone('devices.last_connect_time'), type: 'date' },
	];

	tableConfig: TableConfig = {
		hasExport: false,
		hasPagination: true,
		pageSize: 100,
		hasSelectionColumn: true,
		fileName: this.translate.instant('nav.blocked_devices'),
	};

	tableData: TableData[] = [];
	selectedMacAddresses: string[] = [];
	isAdmin = false;

	constructor(
		private router: Router,
		private translate: TranslateService,
		private usersService: UsersService,
		private deviceService: DeviceService,
		private commonService: CommonService,
		private sideMenuService: SideMenuService,
		private notificationMessageService: NotificationMessageService,
	) { }

	ngOnInit() {
		this.commonService.applyTimeSourceTooltips(this.columnConfig, this.timeSourceByColumn);

		if (!this.usersService.hasAccessPermission(null, 'noc'))
			return this.router.navigate(['/unauthorized']);

		this.sideMenuService.hide();
		this.isAdmin = this.usersService.getCurrentUser().is_admin;
		return this.getBlockedDevices();
	}

	ngAfterViewInit() {
		this.unblockConfirmationPopup.onClose.subscribe((ok: boolean) => {
			if (ok)
				this.unblockSelectedDevices();
		});
	}

	getBlockedDevices() {
		this.selectedMacAddresses = [];
		this.deviceService.getBlockedDevices().subscribe((response: { devices: BlockedDevice[] }) => {
			// Soonest-to-expire first; the table keeps this order until the user sorts a column
			const devices = [...response.devices].sort((a, b) => a.blocked_until - b.blocked_until);
			this.tableData = devices.map(device => this.toTableRow(device));
		});
	}

	toTableRow(device: BlockedDevice): TableData {
		const deviceTypeIndex = this.deviceService.connectivityDeviceTypes.findIndex(type => type.value == device.device_type);
		const deviceTypeItem = this.deviceTypeItems[deviceTypeIndex];

		return {
			id: { value: device.mac_address },
			customer_name: { value: device.customer_name, link: ['/', device.customer_id] },
			site_name: { value: device.site_name, link: ['/', device.customer_id, device.site_id] },
			device_type: { value: deviceTypeItem?.label, sortValue: deviceTypeItem?.value },
			serial_number: { value: device.serial_number },
			mac_address: { value: device.mac_address, link: ['/', device.customer_id, device.site_id, device.mac_address, 'settings'] },
			fw_version: { value: this.commonService.toFixedWithoutRounding(device.fw_version || 0, this.isAdmin ? 4 : 2) },
			// The table's date filter and sort read sortValue as epoch milliseconds
			blocked_until: { value: this.formatTime(device.blocked_until), sortValue: device.blocked_until * 1000 },
			last_connect_time: { value: this.formatTime(device.last_connect_time), sortValue: (device.last_connect_time || 0) * 1000 },
		};
	}

	/** Column header with the user's UTC offset, e.g. "Last Connect Time (UTC-04:00)" */
	withTimeZone(translationKey: string) {
		return `${this.translate.instant(translationKey)} (${this.commonService.showUserTimeZoneReference()})`;
	}

	formatTime(epochSeconds: number | null) {
		return epochSeconds ? moment(epochSeconds * 1000).format('MM/DD/YYYY hh:mm:ss A') : null;
	}

	updateSelectedDevices(rows: TableData[]) {
		this.selectedMacAddresses = rows.map(row => row.id.value);
	}

	exportToCsv() {
		// Set at click time so the Export Time row shows when the file was made, not when the page loaded
		this.tableConfig.csvFileHeaderInfo = [this.translate.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')];
		this.sctCustomTable.generateCsvFile();
	}

	unblockSelectedDevices() {
		this.deviceService.unblockDevicesConnection(this.selectedMacAddresses).subscribe((response: UnblockDevicesResult) => {
			if (response.failed.length) {
				const message = this.translate.instant('devices.unblock_devices_failed', { macAddresses: response.failed.join(', ') });
				this.notificationMessageService.setMessage(message, { clearOnXTimeNavigate: 1 });
			} else {
				this.notificationMessageService.setMessage('globalSuccessMsg', { clearOnXTimeNavigate: 1 });
			}

			this.getBlockedDevices();
		});
	}
}
